/*
 * Order the edit log for the driver so the positional ops don't disturb each
 * other or the reorder positions.
 *
 * The driver resolves page-TARGETED edits (add-*, rotate-page, and the
 * remove/insert anchors) by STABLE page identity, so those are order-robust.
 * The only POSITIONAL op is reorder-page (movePage(from,to) on the live array),
 * whose from/to are positions in the *current display order*. The web computes
 * those positions against an effective-order fold that counts the original page
 * set (incl. pages marked for removal, excl. not-yet-saved inserts).
 *
 * To keep the driver's live positions matching that fold while reorders apply,
 * everything that changes the array LENGTH — insert-page (grows) and
 * remove-page (shrinks) — is pushed AFTER all reorders. Reorders (and the
 * length-neutral rotate/add-* edits) stay in user order; inserts follow in user
 * order; removes go last, descending by index (back-to-front removal never
 * shifts a still-pending target).
 */

import type { Edit } from '@pdf-ticket/pdf-core';

export function sortEditsForSave(edits: ReadonlyArray<Edit>): ReadonlyArray<Edit> {
  const inserts: Array<Extract<Edit, { kind: 'insert-page' }>> = [];
  const removes: Array<Extract<Edit, { kind: 'remove-page' }>> = [];
  const rest: Edit[] = [];
  for (const e of edits) {
    if (e.kind === 'insert-page') inserts.push(e);
    else if (e.kind === 'remove-page') removes.push(e);
    else rest.push(e);
  }
  removes.sort((a, b) => b.page - a.page);
  return [...rest, ...inserts, ...removes];
}
